/**
 * CollapsibleTreePanel — the shared left-rail shell around <AssetNavTree>, used by
 * BOTH the Assets Overview and the Asset Detail pages (asset-domain-v2-design.md
 * §3.2 tree + Figma collapse ◀). Expanded it shows a "Menu" header with a collapse
 * ◀ button + the tree; collapsed it shrinks to a thin bar with a ▶ expand button
 * (ChatPanel collapse pattern). Collapsed state persists to sessionStorage under one
 * shared key, so the choice is remembered across pages + navigation.
 *
 * The panel owns ONLY the collapse chrome; the tree itself (data, expand/select,
 * asset-click → detail navigation) stays in AssetNavTree, passed as children so this
 * shell is agnostic to what it wraps.
 */
import { useCallback, useSyncExternalStore, type ReactNode } from 'react';
import { Button, ButtonDropdown } from '@amzn/awsui-components-react';
import './CollapsibleTreePanel.css';

const STORAGE_KEY = 'ivt.assetTree.collapsed';
const MODE_STORAGE_KEY = 'ivt.assetTree.mode';

export type TreeMode = 'simplified' | 'structured' | 'grow' | 'storyline';

// Tiny shared store so the collapsed choice is live across both pages' panels and
// survives navigation (sessionStorage-backed, same idiom as useMotionFilterState).
const listeners = new Set<() => void>();
let cached: boolean | null = null;

function read(): boolean {
  if (cached !== null) return cached;
  try {
    cached = sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    cached = false;
  }
  return cached;
}
function write(next: boolean) {
  cached = next;
  try {
    sessionStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* private mode / quota — just won't persist */
  }
  listeners.forEach((cb) => cb());
}
function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Shared collapsed state + setter (sessionStorage-backed, live across panels). */
export function useTreeCollapsed(): [boolean, (next: boolean) => void] {
  const collapsed = useSyncExternalStore(subscribe, read, () => false);
  const set = useCallback((next: boolean) => write(next), []);
  return [collapsed, set];
}

/** Test-only: reset the module cache so a test's sessionStorage.clear() isn't
 *  shadowed by a stale cached value from a prior test. */
export function __resetTreeCollapsedForTesting(): void {
  cached = null;
  listeners.clear();
}

// ── Tree mode (Simplified / Structured / GROW / Storyline) — a second shared store, same idiom, so
// the mode toggle can live in the panel's "Menu" header (chrome) while AssetNavTree
// (the tree body) reads the same live value. localStorage-backed (persists across
// sessions, matching the previous behavior); default 'simplified'.
const modeListeners = new Set<() => void>();
let modeCached: TreeMode | null = null;

function readMode(): TreeMode {
  if (modeCached !== null) return modeCached;
  try {
    const v = localStorage.getItem(MODE_STORAGE_KEY);
    // 'menu' is the legacy id for the Storyline view (renamed → 'storyline'); map it
    // so users who last picked it keep the Storyline view instead of silently
    // dropping back to Simplified.
    if (v === 'menu') modeCached = 'storyline';
    else modeCached = v === 'structured' || v === 'grow' || v === 'storyline' ? v : 'simplified';
  } catch {
    modeCached = 'simplified';
  }
  return modeCached;
}
function writeMode(next: TreeMode) {
  modeCached = next;
  try {
    localStorage.setItem(MODE_STORAGE_KEY, next);
  } catch {
    /* private mode / quota — just won't persist */
  }
  modeListeners.forEach((cb) => cb());
}
function subscribeMode(cb: () => void): () => void {
  modeListeners.add(cb);
  return () => modeListeners.delete(cb);
}

/** Shared tree mode + setter (localStorage-backed, live across the panel header +
 *  the tree body). */
export function useTreeMode(): [TreeMode, (next: TreeMode) => void] {
  const mode = useSyncExternalStore(subscribeMode, readMode, () => 'simplified' as TreeMode);
  const set = useCallback((next: TreeMode) => writeMode(next), []);
  return [mode, set];
}

/** Test-only: reset the mode store cache (mirror of the collapsed reset). */
export function __resetTreeModeForTesting(): void {
  modeCached = null;
  modeListeners.clear();
}

/** The tree mode toggle (Simplified / Structured / GROW / Storyline) — a ≡ icon ButtonDropdown. Lives in the
 *  panel's "Menu" header row (moved out of the tree body). */
export function TreeModeToggle() {
  const [mode, setMode] = useTreeMode();
  return (
    <ButtonDropdown
      variant="icon"
      iconName="list-view"
      ariaLabel="切换导航树视图"
      items={[
        { id: 'simplified', text: 'Simplified Menu', itemType: 'checkbox', checked: mode === 'simplified' },
        { id: 'structured', text: 'Structured Menu', itemType: 'checkbox', checked: mode === 'structured' },
        { id: 'grow', text: 'GROW Menu', itemType: 'checkbox', checked: mode === 'grow' },
        { id: 'storyline', text: 'Storyline Menu', itemType: 'checkbox', checked: mode === 'storyline' },
      ]}
      onItemClick={({ detail }) => setMode(detail.id as TreeMode)}
    />
  );
}

export default function CollapsibleTreePanel({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useTreeCollapsed();

  if (collapsed) {
    return (
      <aside className="ivt-treepanel ivt-treepanel--collapsed">
        <Button
          variant="icon"
          iconName="angle-right"
          ariaLabel="展开分类树"
          onClick={() => setCollapsed(false)}
        />
      </aside>
    );
  }

  return (
    <aside className="ivt-treepanel">
      <div className="ivt-treepanel__head">
        <span className="ivt-treepanel__title">Menu</span>
        <div className="ivt-treepanel__head-actions">
          {/* Tree mode toggle now lives on the Menu row (was
              floating at the tree's top-right). */}
          <TreeModeToggle />
          <Button
            variant="icon"
            iconName="angle-left"
            ariaLabel="收起分类树"
            onClick={() => setCollapsed(true)}
          />
        </div>
      </div>
      <div className="ivt-treepanel__body">{children}</div>
    </aside>
  );
}
